import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { SEO, Breadcrumb } from '@/components/seo';
import { FAQSchema } from '@/components/seo/Schema';
import {
  ArrowRight,
  Loader2,
  ExternalLink,
  Trophy,
  CheckCircle,
  XCircle,
  AlertCircle,
  Target,
  Shield,
  Droplet,
  ChevronDown,
  TrendingDown,
  Info,
  FlaskConical,
} from 'lucide-react';
import { buttonStyles } from '@/components/ui';
import { Stats, Testimonials, BeforeAfter } from '@/components/sections';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = import.meta.env.VITE_API_URL || 'https://lionfish-app-zwr6q.ondigitalocean.app/api/v1';

interface FAQItem {
  q: string;
  a: string;
}

interface DetectorData {
  id: string;
  slug: string;
  name: string;
  url: string;
  description: string | null;
  longDescription: string | null;
  faq: FAQItem[] | null;
  eloRating: number;
  totalVotes: number;
  wins: number;
  losses: number;
  ties: number;
  accuracy: number | null;
  falsePositiveRate: number | null;
  falseNegativeRate: number | null;
  totalTested: number;
  logoUrl: string | null;
  rank: number;
}

interface PlatformData {
  slug: string;
  name: string;
  url: string;
  isConfirmed: boolean;
}

interface ModelResult {
  slug: string;
  name: string;
  vendor: string;
  total: number;
  correct: number;
  successRate: number;
  avgScore: number | null;
  logoUrl: string | null;
}

interface WatermarkResult {
  slug: string;
  name: string;
  developer: string | null;
  total: number;
  correct: number;
  successRate: number;
  avgScore: number | null;
  logoUrl: string | null;
}

interface RelatedDetector {
  slug: string;
  name: string;
  eloRating: number;
  accuracy: number | null;
  logoUrl: string | null;
}

export function DetectorPage() {
  const { slug } = useParams<{ slug: string }>();
  const [detector, setDetector] = useState<DetectorData | null>(null);
  const [platforms, setPlatforms] = useState<PlatformData[]>([]);
  const [byModel, setByModel] = useState<ModelResult[]>([]);
  const [byWatermark, setByWatermark] = useState<WatermarkResult[]>([]);
  const [relatedDetectors, setRelatedDetectors] = useState<RelatedDetector[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/pages/detectors/${slug}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to load detector');
        }

        setDetector(data.detector);
        setPlatforms(data.platforms || []);
        setByModel(data.byModel || []);
        setByWatermark(data.byWatermark || []);
        setRelatedDetectors(data.relatedDetectors || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug]);

  const getRankBadge = (rank: number) => {
    if (rank === 1) return <span className="text-4xl">🥇</span>;
    if (rank === 2) return <span className="text-4xl">🥈</span>;
    if (rank === 3) return <span className="text-4xl">🥉</span>;
    return <span className="text-3xl font-bold text-gray-400">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  if (error || !detector) {
    return <Navigate to="/" replace />;
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Detectors', href: '/' },
    { label: detector.name },
  ];

  return (
    <>
      <SEO
        title={`${detector.name} AI Detector Review — Accuracy, Benchmark & Comparison | AI Detector Arena`}
        description={detector.description || `${detector.name} accuracy review: see benchmark results, false positive rate, and how it compares to ${relatedDetectors.length > 0 ? relatedDetectors.length : 'other'} AI detectors.`}
        canonical={`/detectors/${detector.slug}`}
      />

      {/* FAQ Schema for rich snippets */}
      {detector.faq && detector.faq.length > 0 && (
        <FAQSchema
          questions={detector.faq.map(item => ({
            question: item.q,
            answer: item.a,
          }))}
        />
      )}

      {/* Hero */}
      <section className="section bg-gradient-to-b from-red-50 via-white to-white">
        <div className="container">
          <Breadcrumb items={breadcrumbItems} />

          <div className="max-w-4xl mx-auto mt-8">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {detector.logoUrl ? (
                  <img
                    src={detector.logoUrl}
                    alt={`${detector.name} logo`}
                    className="w-16 h-16 object-contain"
                  />
                ) : (
                  getRankBadge(detector.rank)
                )}
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 flex items-center gap-2">
                    {detector.name} AI Detector
                    <a href={detector.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-5 h-5 text-gray-400 hover:text-purple-500" />
                    </a>
                  </h1>
                  <p className="text-gray-500 mt-1">Benchmark Results & Accuracy Stats</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-purple-600">{detector.eloRating}</div>
                <div className="text-sm text-gray-500">Elo Rating</div>
              </div>
            </div>

            {detector.description && (
              <p className="text-gray-600 mt-6 max-w-2xl">
                {detector.description}
              </p>
            )}
          </div>

        </div>
      </section>

      {/* Stats Grid */}
      <section className="section bg-white py-8">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-500">
                  {detector.accuracy?.toFixed(1) || '—'}%
                </div>
                <div className="text-gray-500 text-sm">Accuracy</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-red-500">
                  {detector.falsePositiveRate?.toFixed(1) || '—'}%
                </div>
                <div className="text-gray-500 text-sm">False Positive</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-yellow-500">
                  {detector.falseNegativeRate?.toFixed(1) || '—'}%
                </div>
                <div className="text-gray-500 text-sm">False Negative</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-500">{detector.totalTested}</div>
                <div className="text-gray-500 text-sm">Images Tested</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Verdict / Summary */}
      {detector.accuracy !== null && byModel.length > 0 && (() => {
        const sorted = [...byModel].sort((a, b) => b.successRate - a.successRate);
        const bestModel = sorted[0];
        const worstModel = sorted[sorted.length - 1];
        const fp = detector.falsePositiveRate;
        const fpComment = fp !== null
          ? fp < 5
            ? 'rarely misidentifies real photos'
            : fp <= 15
              ? 'occasionally flags real photos as AI-generated'
              : 'frequently misidentifies real photos as AI-generated'
          : null;

        return (
          <section className="section bg-white py-8">
            <div className="container">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Info className="w-6 h-6 text-blue-500" />
                  Verdict
                </h2>
                <div className="bg-blue-50 rounded-xl p-6">
                  <p className="text-gray-700 leading-relaxed">
                    {detector.name} ranks <strong>#{detector.rank}</strong> among tested AI detectors
                    with an overall accuracy of <strong>{detector.accuracy?.toFixed(1)}%</strong> across{' '}
                    <strong>{detector.totalTested}</strong> images.
                    {byModel.length > 1 && (
                      <>
                        {' '}It performs strongest against{' '}
                        <Link to={`/models/${bestModel.slug}`} className="text-blue-600 hover:underline font-medium">
                          {bestModel.name}
                        </Link>{' '}
                        images ({bestModel.successRate}% detection rate) but struggles with{' '}
                        <Link to={`/models/${worstModel.slug}`} className="text-blue-600 hover:underline font-medium">
                          {worstModel.name}
                        </Link>{' '}
                        content (only {worstModel.successRate}% detected).
                      </>
                    )}
                    {fpComment && (
                      <>
                        {' '}With a {detector.falsePositiveRate?.toFixed(1)}% false positive rate,
                        it {fpComment}.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Top 3 Models That Bypass This Detector */}
      {byModel.length > 0 && (() => {
        const topBypass = [...byModel]
          .sort((a, b) => a.successRate - b.successRate)
          .slice(0, 3);
        return (
          <section className="section bg-white py-8">
            <div className="container">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <TrendingDown className="w-6 h-6 text-green-500" />
                  Hardest to Detect Models
                </h2>
                <p className="text-gray-500 mb-6 text-sm">
                  AI models with the lowest detection rate by {detector.name}.
                </p>

                <div className="grid md:grid-cols-3 gap-4">
                  {topBypass.map((model, i) => (
                    <Link
                      key={model.slug}
                      to={`/models/${model.slug}`}
                      className="bg-green-50 rounded-xl p-5 hover:bg-green-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-green-600 font-bold text-lg">#{i + 1}</span>
                        {model.logoUrl && (
                          <img src={model.logoUrl} alt={model.name} className="w-8 h-8 object-contain" />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{model.name}</div>
                          <div className="text-xs text-gray-500">{model.vendor}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-green-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${model.successRate}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-green-600">
                          {model.successRate}% detected
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Arena Performance */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" />
              Arena Performance
            </h2>

            <div className="bg-white rounded-xl p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-500">{detector.wins}</div>
                  <div className="text-gray-500 text-sm">Wins</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-500">{detector.losses}</div>
                  <div className="text-gray-500 text-sm">Losses</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-400">{detector.ties}</div>
                  <div className="text-gray-500 text-sm">Ties</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                <span className="text-gray-500">Total Battles: </span>
                <span className="font-medium">{detector.totalVotes}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms using this detector */}
      {platforms.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Shield className="w-6 h-6 text-orange-500" />
                Platforms Using {detector.name}
              </h2>

              <div className="grid md:grid-cols-3 gap-4">
                {platforms.map((platform) => (
                  <Link
                    key={platform.slug}
                    to={`/bypass/${platform.slug}`}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="font-medium text-gray-900">{platform.name}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      {platform.isConfirmed ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Confirmed
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          Suspected
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Detection by AI Model */}
      {byModel.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Target className="w-6 h-6 text-purple-500" />
                Detection by AI Model
              </h2>

              <div className="bg-white rounded-xl p-6">
                <div className="space-y-3">
                  {byModel.map((model) => (
                    <Link
                      key={model.slug}
                      to={`/models/${model.slug}`}
                      className="flex items-center justify-between hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {model.logoUrl ? (
                          <img src={model.logoUrl} alt={model.name} className="w-5 h-5 object-contain" />
                        ) : model.successRate === 100 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : model.successRate === 0 ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-yellow-500" />
                        )}
                        <div>
                          <span className="font-medium text-gray-900">{model.name}</span>
                          <span className="text-xs text-gray-500 ml-2">{model.vendor}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              model.successRate >= 70 ? 'bg-green-500' :
                              model.successRate >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${model.successRate}%` }}
                          />
                        </div>
                        <span className="text-sm w-12 text-right">
                          {model.successRate}%
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Detection by Watermark */}
      {byWatermark.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Droplet className="w-6 h-6 text-blue-500" />
                Detection by Watermark
              </h2>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="space-y-3">
                  {byWatermark.map((wm) => (
                    <Link
                      key={wm.slug}
                      to={`/watermarks/${wm.slug}`}
                      className="flex items-center justify-between hover:bg-white p-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {wm.logoUrl ? (
                          <img src={wm.logoUrl} alt={wm.name} className="w-5 h-5 object-contain" />
                        ) : wm.successRate >= 70 ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : wm.successRate < 30 ? (
                          <XCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-yellow-500" />
                        )}
                        <div>
                          <span className="font-medium text-gray-900">{wm.name}</span>
                          {wm.developer && (
                            <span className="text-xs text-gray-500 ml-2">{wm.developer}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              wm.successRate >= 70 ? 'bg-green-500' :
                              wm.successRate >= 40 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${wm.successRate}%` }}
                          />
                        </div>
                        <span className="text-sm w-12 text-right">
                          {wm.successRate}%
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* Benchmark Methodology */}
      <section className="section bg-gray-50">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-purple-500" />
              Benchmark Methodology
            </h2>
            <div className="bg-white rounded-xl p-6 space-y-4">
              <p className="text-gray-700 leading-relaxed">
                {detector.name} was evaluated on a curated dataset of{' '}
                <strong>{detector.totalTested}</strong> images
                {byModel.length > 0 && (
                  <> across <strong>{byModel.length}</strong> AI image generators</>
                )}
                {byWatermark.length > 0 && (
                  <> and <strong>{byWatermark.length}</strong> watermark types</>
                )}
                , including models like Midjourney, Stable Diffusion, DALL-E 3, Flux, and others.
                The dataset also includes real photographs from verified sources to measure false positive rates.
              </p>
              <p className="text-gray-700 leading-relaxed">
                All detectors are tested under identical conditions using the same images.
                We record each detector's classification (AI or Real) and confidence score,
                then compute three key metrics:{' '}
                <strong>Accuracy</strong> (percentage of correct predictions),{' '}
                <strong>False Positive Rate</strong> (real images incorrectly flagged as AI), and{' '}
                <strong>False Negative Rate</strong> (AI images missed by the detector).
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                See full benchmark methodology
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <Stats />

      {/* Before/After */}
      <BeforeAfter />

      {/* SEO Text */}
      {detector.longDescription && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-3xl mx-auto prose prose-gray">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {detector.longDescription}
              </ReactMarkdown>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {detector.faq && detector.faq.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {detector.faq.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <span className="font-medium text-gray-900">{item.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-500 transition-transform ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-5 text-gray-600">
                        {item.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Related Detectors */}
      {relatedDetectors.length > 0 && (
        <section className="section bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Other AI Detectors
              </h2>

              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {relatedDetectors.map((rd) => (
                  <Link
                    key={rd.slug}
                    to={`/detectors/${rd.slug}`}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors flex items-start gap-3"
                  >
                    {rd.logoUrl && (
                      <img src={rd.logoUrl} alt={rd.name} className="w-8 h-8 object-contain flex-shrink-0" />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">{rd.name}</h3>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-purple-600 font-medium">
                          {rd.eloRating} Elo
                        </span>
                        {rd.accuracy !== null && (
                          <span className="text-gray-500 ml-2">
                            {rd.accuracy.toFixed(0)}% accuracy
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <Testimonials />

      {/* CTA */}
      <section className="section bg-primary-600">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Test {detector.name} in the Arena
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Upload your image and see how {detector.name} performs against other detectors in real-time.
          </p>
          <Link
            to="/arena"
            className={buttonStyles({
              variant: 'secondary',
              size: 'lg',
              className: 'bg-white text-primary-600 hover:bg-gray-100',
            })}
          >
            Start a Battle
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
}

export default DetectorPage;
